import { sankeyLinkHorizontal } from "d3-sankey";
import { useState, useMemo, useEffect, useRef } from "react";
import { path as d3Path } from "d3-path";
import Tooltip from "@mui/material/Tooltip";
import { fontSizes } from "../../typography";
import type { SankeyNode, SankeyLink, SankeyGraph, RouteWithWeight } from "./types";
import { dataVizClassificationPalette } from "@atlas-int/tokens";

// Constants
const COLOR_PALETTE = dataVizClassificationPalette;
const PAUSE_DURATION = 3000; // ms between animation cycles
const TARGET_PARTICLE_COUNT = 100;

const getColorByDepth = (depth: number): string => {
  return COLOR_PALETTE[depth % COLOR_PALETTE.length];
};

const isRootNode = (node: SankeyNode) =>
  !node.targetLinks || node.targetLinks.length === 0;

// Types
type SankeyProps = {
  width: number;
  height: number;
  diagramHeight?: number;
  currentStepIndex?: string;
  routeWeights?: Record<string, number>;
  isNarrow: boolean;
  embed?: boolean;
  translateY?: number;
  graph: SankeyGraph;
  onDepthCenters?: (centers: Array<{ depth: number; x: number }>) => void;
};

type Particle = {
  id: string;
  routeIndex: number;
  progress: number;
  speed: number;
  offset: number;
  createdAt: number;
  lastNodeIndex: number;
};

export default function Sankey(props: SankeyProps) {
  const {
    width,
    height,
    diagramHeight,
    currentStepIndex,
    routeWeights,
    isNarrow,
    embed = false,
    translateY = 0,
    graph,
    onDepthCenters,
  } = props;

  const layoutHeight = diagramHeight || height;
  const NODE_HEIGHT = layoutHeight / 14;
  const verticalOffset = (height - layoutHeight) / 2;
  const particleSize = isNarrow ? 6 : 8;

  const routes = useMemo(() => {
    const walk = (node: SankeyNode): RouteWithWeight[] => {
      if (!node.sourceLinks || node.sourceLinks.length === 0) {
        return [{ nodes: [node], weight: 1, routeKey: node.name || String(node.id) }];
      }
      return node.sourceLinks.flatMap((link: SankeyLink) => {
        const childRoutes = walk(link.target);
        const linkWeight = link["weighted-value"] || 0;
        return childRoutes.map((route) => ({
          nodes: [node, ...route.nodes],
          weight: linkWeight * route.weight,
          routeKey: `${node.name || node.id}->${route.routeKey}`,
        }));
      });
    };

    const root = graph.nodes.find((n: SankeyNode) => isRootNode(n));
    if (!root) return [];

    let allRoutes = walk(root);

    // Apply custom route weights if provided
    if (routeWeights) {
      allRoutes = allRoutes.map((route) => {
        const customWeight = routeWeights[route.routeKey];
        return customWeight !== undefined
          ? { ...route, weight: customWeight }
          : route;
      });
    }

    return allRoutes;
  }, [graph.nodes, routeWeights]);

  // Report per-depth horizontal centers to parent
  useEffect(() => {
    if (!onDepthCenters || !graph?.nodes) return;

    const depths = Array.from(
      new Set(graph.nodes.map((n: SankeyNode) => Number(n.depth))),
    ).sort((a, b) => Number(a) - Number(b));
    const centers = depths.map((depth) => {
      const nodesAtDepth = graph.nodes.filter(
        (n: SankeyNode) => Number(n.depth) === depth,
      );
      const minX0 = Math.min(...nodesAtDepth.map((n: SankeyNode) => n.x0));
      const maxX1 = Math.max(...nodesAtDepth.map((n: SankeyNode) => n.x1));
      return { depth: Number(depth), x: (minX0 + maxX1) / 2 };
    });
    onDepthCenters(centers);
  }, [graph, onDepthCenters]);

  // Precompute normalized entry thresholds per route
  const routeEntryThresholds = useMemo(() => {
    return routes.map((route: RouteWithWeight) => {
      const nodesInRoute = route.nodes || [];
      if (nodesInRoute.length <= 1) return [0];

      const linkLengths = nodesInRoute
        .slice(0, -1)
        .map((src: SankeyNode, i: number) => {
          const tgt = nodesInRoute[i + 1];
          return Math.max((tgt?.x0 ?? 0) - (src?.x1 ?? 0), 0);
        });

      const totalLength = linkLengths.reduce((s, v) => s + v, 0) || 1;
      const thresholds = [0];
      let accum = 0;

      linkLengths.forEach((len) => {
        accum += len / totalLength;
        thresholds.push(Math.min(accum, 1));
      });

      return thresholds;
    });
  }, [routes]);

  // Create path function for particles
  const createRoutePath = (route: RouteWithWeight, offset: number) => {
    const nodes = route.nodes;
    const p = d3Path();

    nodes.forEach((node: SankeyNode, i: number) => {
      const bandHeight = node.y1 - node.y0;
      const y = node.y0 + bandHeight * offset;

      if (i === 0) {
        p.moveTo(node.x1, y);
      } else {
        const prevNode = nodes[i - 1];
        const prevBandHeight = prevNode.y1 - prevNode.y0;
        const prevY = prevNode.y0 + prevBandHeight * offset;
        const xi = (prevNode.x1 + node.x0) / 2;

        p.bezierCurveTo(xi, prevY, xi, y, node.x0, y);
        if (i < nodes.length - 1) {
          p.lineTo(node.x1, y);
        }
      }
    });

    return p.toString();
  };

  // Helper to get point on path at progress t (0..1)
  const getPointOnPath = (pathString: string, t: number) => {
    if (typeof document === "undefined") return { x: 0, y: 0 };
    const svgPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );
    svgPath.setAttribute("d", pathString);
    const len = svgPath.getTotalLength();
    const pt = svgPath.getPointAtLength(t * len);
    return { x: pt.x, y: pt.y };
  };

  // Particle state management
  const [particles, setParticles] = useState<Particle[]>([]);

  // Track particle counts per end node
  const [nodeCounts, setNodeCounts] = useState<Record<string, number>>({});

  // Track total particles created across all time using a ref (updates immediately)
  const totalParticlesCreatedRef = useRef(0);
  const pauseStartTimeRef = useRef<number | null>(null);
  // Accumulate node count updates to avoid nested setState
  const pendingNodeUpdatesRef = useRef<Record<string, number>>({});

  // Compute route distribution based on weights
  const routeDistribution = useMemo(() => {
    if (routes.length === 0) return [];

    const totalWeight = routes.reduce(
      (sum: number, route: RouteWithWeight) => sum + route.weight,
      0,
    );

    // Create cumulative distribution for weighted sampling
    let cumSum = 0;
    const distribution = routes.map((route: RouteWithWeight) => {
      cumSum += route.weight / totalWeight;
      return cumSum;
    });

    return distribution;
  }, [routes]);

  useEffect(() => {
    let animationFrameId: number;
    let lastTime: number | null = null;

    const animate = (currentTime: number) => {
      // Handle pause between cycles
      if (pauseStartTimeRef.current !== null) {
        if (currentTime - pauseStartTimeRef.current < PAUSE_DURATION) {
          animationFrameId = requestAnimationFrame(animate);
          return;
        }
        // Resume after pause
        setNodeCounts({});
        pauseStartTimeRef.current = null;
        lastTime = null;
        totalParticlesCreatedRef.current = 0;
      }

      if (lastTime === null) lastTime = currentTime;
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      setParticles((prev) => {
        let active = prev.filter((p) => p.progress < 1);

        // Check if cycle complete - trigger pause
        if (
          prev.length > 0 &&
          active.length === 0 &&
          pauseStartTimeRef.current === null
        ) {
          pauseStartTimeRef.current = currentTime;
          return active;
        }

        // Create new particles if not paused and under target
        if (
          pauseStartTimeRef.current === null &&
          totalParticlesCreatedRef.current < TARGET_PARTICLE_COUNT &&
          routes.length > 0
        ) {
          let newParticlesThisFrame = 0;

          while (
            active.length < TARGET_PARTICLE_COUNT &&
            totalParticlesCreatedRef.current + newParticlesThisFrame <
              TARGET_PARTICLE_COUNT
          ) {
            const normalizedPosition =
              ((totalParticlesCreatedRef.current + newParticlesThisFrame) /
                TARGET_PARTICLE_COUNT) %
              1.0;

            let selectedRouteIndex = 0;
            for (let i = 0; i < routeDistribution.length; i++) {
              if (normalizedPosition < routeDistribution[i]) {
                selectedRouteIndex = i;
                break;
              }
            }

            active.push({
              id: `${Date.now()}_${Math.random()}`,
              routeIndex: selectedRouteIndex,
              progress: Math.random() * -0.5,
              speed: 0.0001,
              offset: 0.3 + Math.random() * 0.4,
              createdAt: currentTime,
              lastNodeIndex: -1,
            });
            newParticlesThisFrame++;
          }

          totalParticlesCreatedRef.current += newParticlesThisFrame;
        }

        // Progress particles and accumulate node updates in ref (not state)
        const updated = active.map((p) => {
          const newProgress = Math.min(1, p.progress + p.speed * deltaTime);
          const route = routes[p.routeIndex];
          const thresholds = routeEntryThresholds[p.routeIndex] || [0];
          let newLastNodeIndex = p.lastNodeIndex;

          if (route && newProgress >= 0) {
            let currentNodeIndex = 0;
            for (let i = thresholds.length - 1; i >= 0; i--) {
              if (newProgress >= thresholds[i]) {
                currentNodeIndex = i;
                break;
              }
            }

            // Accumulate counts in ref for newly entered nodes
            if (
              currentNodeIndex > p.lastNodeIndex &&
              currentNodeIndex < route.nodes.length
            ) {
              for (let i = p.lastNodeIndex + 1; i <= currentNodeIndex; i++) {
                const node = route.nodes[i];
                if (node) {
                  pendingNodeUpdatesRef.current[node.id] =
                    (pendingNodeUpdatesRef.current[node.id] || 0) + 1;
                }
              }
              newLastNodeIndex = currentNodeIndex;
            }
          }

          return {
            ...p,
            progress: newProgress,
            lastNodeIndex: newLastNodeIndex,
          };
        });

        return updated;
      });

      // Apply accumulated node count updates outside of setParticles
      if (Object.keys(pendingNodeUpdatesRef.current).length > 0) {
        const updates = pendingNodeUpdatesRef.current;
        pendingNodeUpdatesRef.current = {};
        setNodeCounts((prevCounts) => {
          const newCounts = { ...prevCounts };
          for (const [nodeId, increment] of Object.entries(updates)) {
            newCounts[nodeId] = (newCounts[nodeId] || 0) + increment;
          }
          return newCounts;
        });
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [routes, routeDistribution, routeEntryThresholds]);

  const content = (
    <g transform={`translate(0, ${verticalOffset})`}>
      <g fill="none" strokeOpacity={0.5}>
        {graph.links.map((d: SankeyLink) => (
          <path
            key={`${d.source.id}-${d.target.id}`}
            d={sankeyLinkHorizontal()(d) ?? ""}
            stroke="#ddddddff"
            opacity={d.step === currentStepIndex ? 0.8 : 0.4}
            strokeWidth={NODE_HEIGHT}
          />
        ))}
      </g>
      {/* Animated particles moving through routes */}
      <g>
        {particles
          .filter((particle) => particle.progress >= 0 && particle.progress < 1)
          .map((particle) => {
            const route = routes[particle.routeIndex];
            if (!route) return null;

            // Determine current depth based on particle position
            const routeNodes = route.nodes;
            const particleProgress = particle.progress; // 0 to 1 across entire route
            const thresholds = routeEntryThresholds[particle.routeIndex] || [0];

            // Determine which node the particle has reached based on thresholds
            let currentNodeIndex = 0;
            for (let i = thresholds.length - 1; i >= 0; i--) {
              if (particleProgress >= thresholds[i]) {
                currentNodeIndex = i;
                break;
              }
            }

            // Use the color of the next node the particle is going to cross
            const nextNodeIndex = Math.min(
              currentNodeIndex + 1,
              routeNodes.length - 1,
            );
            const colorNode = routeNodes[nextNodeIndex];

            const pathString = createRoutePath(route, particle.offset);
            const pos = getPointOnPath(pathString, particle.progress);
            return (
              <rect
                key={particle.id}
                x={pos.x - particleSize / 2}
                y={pos.y - particleSize / 2}
                width={particleSize}
                height={particleSize}
                fill={getColorByDepth(colorNode.depth)}
                opacity={0.8}
              />
            );
          })}
      </g>
      <g>
        {graph.nodes.map((d: SankeyNode) => {
          // Check if this is an end node (no outgoing links)
          const isEndNode = !d.sourceLinks || d.sourceLinks.length === 0;
          // Check if this is the root node (no incoming links)
          const isRootNodeFlag = !d.targetLinks || d.targetLinks.length === 0;
          const isMiddleNode = !isRootNodeFlag && !isEndNode;

          // Calculate percentage for fill animation
          let fillPercentage = 0;
          if (!isRootNodeFlag) {
            const nodeCount = nodeCounts[d.id] || 0;
            const totalParticlesAll = graph.nodes
              .filter((n: SankeyNode) => !n.targetLinks || n.targetLinks.length === 0)
              .reduce(
                (sum: number, n: SankeyNode) => sum + (nodeCounts[n.id] || 0),
                0,
              );
            fillPercentage =
              totalParticlesAll > 0 ? nodeCount / totalParticlesAll : 0;
          }

          const nodeHeight = d.y1 - d.y0;
          const fillHeight = nodeHeight * fillPercentage;
          let fontSize = isNarrow
            ? fontSizes.figure4NodeLabelsNarrow
            : fontSizes.figure4NodeLabels;

          return (
            <Tooltip
              key={`node-tooltip-${d.id}`}
              title={
                <div
                  style={{
                    fontSize: fontSize,
                    whiteSpace: "pre-line",
                    lineHeight: 1.3,
                  }}
                >
                  {`${d.name || d.id}\n${d.shortname || ""}`}
                </div>
              }
              placement="top"
              arrow
              enterDelay={0}
              leaveDelay={0}
              disableHoverListener={!isMiddleNode}
            >
              <g>
                {/* Background/outline rectangle */}
                <rect
                  x={d.x0}
                  y={d.y0}
                  height={nodeHeight}
                  width={d.x1 - d.x0}
                  fill={isRootNodeFlag ? getColorByDepth(d.depth) : "#ffffff"}
                  stroke={isRootNodeFlag ? "none" : getColorByDepth(d.depth)}
                  strokeWidth={isRootNodeFlag ? 0 : 2}
                  opacity={1}
                  style={{ cursor: isMiddleNode ? "pointer" : "default" }}
                />
                {/* Fill rectangle that grows based on percentage */}
                {!isRootNodeFlag && fillHeight > 0 && (
                  <rect
                    x={d.x0}
                    y={d.y1 - fillHeight}
                    height={fillHeight}
                    width={d.x1 - d.x0}
                    fill={getColorByDepth(d.depth)}
                    opacity={1}
                    style={{ cursor: isMiddleNode ? "pointer" : "default" }}
                  />
                )}
              </g>
            </Tooltip>
          );
        })}
      </g>
      <g>
        {graph.nodes.map((d: SankeyNode, nodeIndex: number) => {
          // Check if this is an end node (no outgoing links)
          const isEndNode = !d.sourceLinks || d.sourceLinks.length === 0;
          // Check if this is the root node (no incoming links)
          const isRootNodeFlag = !d.targetLinks || d.targetLinks.length === 0;
          const isMiddleNode = !isRootNodeFlag && !isEndNode;
          const isLeft = d.x0 < width / 2;
          const x = isRootNodeFlag ? d.x0 - 40 : isLeft ? d.x1 + 6 : d.x0 - 6;
          const anchor = isRootNodeFlag ? "middle" : isLeft ? "start" : "end";

          // Calculate total particles across all roots for percentage calculation
          const totalParticlesAll = graph.nodes
            .filter((n: SankeyNode) => !n.targetLinks || n.targetLinks.length === 0)
            .reduce((sum: number, n: SankeyNode) => sum + (nodeCounts[n.id] || 0), 0);

          const nodeCount = nodeCounts[d.id] || 0;
          const percentage =
            totalParticlesAll > 0
              ? ((nodeCount / totalParticlesAll) * 100).toFixed(0)
              : 0;

          // Track end node index for spacing between end node labels
          const endNodeIndex = isEndNode
            ? graph.nodes.filter((n: SankeyNode, idx: number) => {
                return (
                  idx < nodeIndex && (!n.sourceLinks || n.sourceLinks.length === 0)
                );
              }).length
            : 0;
          const endNodeYOffset = isEndNode ? endNodeIndex * 10 : 0; // Add 15px spacing per end node

          return (
            <g key={d.id}>
              {isRootNodeFlag && (
                <text
                  x={x}
                  y={(d.y1 + d.y0) / 2 + endNodeYOffset}
                  textAnchor={anchor}
                  fontSize={
                    isNarrow
                      ? fontSizes.figure4NodeLeftLabelNarrow
                      : fontSizes.figure4NodeLeftLabel
                  }
                >
                  {(() => {
                    const words = d.shortname ? d.shortname.split(/\s+/) : [];
                    const maxWordsPerLine = 1;
                    const lines: string[] = [];

                    for (let j = 0; j < words.length; j += maxWordsPerLine) {
                      lines.push(words.slice(j, j + maxWordsPerLine).join(" "));
                    }

                    const lineSpacing = 14;
                    const numLines = 1 + lines.length;
                    const startOffset = (-(numLines - 1) * lineSpacing) / 2;

                    return (
                      <>
                        <tspan x={x} dy={startOffset}>
                          {d.name || d.id}
                        </tspan>
                        {lines.map((line: string) => (
                          <tspan
                            key={line}
                            x={x}
                            dy={lineSpacing}
                            fontSize={
                              isNarrow
                                ? fontSizes.figure4NodeLeftLabelNarrow
                                : fontSizes.figure4NodeLeftLabel
                            }
                            fill="#666666"
                          >
                            {line}
                          </tspan>
                        ))}
                      </>
                    );
                  })()}
                </text>
              )}
              {isMiddleNode && (
                <text
                  x={(d.x0 + d.x1) / 2}
                  y={d.y1 + 14}
                  textAnchor="middle"
                  fontSize={
                    isNarrow
                      ? fontSizes.figure4NodeLabelsNarrow
                      : fontSizes.figure4NodeLabels
                  }
                  fontWeight={600}
                  fill={getColorByDepth(d.depth)}
                >
                  {percentage}%
                </text>
              )}
              {isEndNode && (
                <g>
                  {(() => {
                    const words = d.shortname ? d.shortname.split(/\s+/) : [];
                    const maxWordsPerLine = 2;
                    const lines: string[] = [];

                    for (let j = 0; j < words.length; j += maxWordsPerLine) {
                      lines.push(words.slice(j, j + maxWordsPerLine).join(" "));
                    }

                    const lineSpacing = 11;
                    const numLines = 1 + lines.length;
                    const labelStartOffset =
                      (-(numLines - 1) * lineSpacing) / 2;

                    return (
                      <>
                        <text
                          x={d.x1 + 6}
                          y={(d.y1 + d.y0) / 2}
                          dy="0.35em"
                          textAnchor="start"
                          fontSize={
                            isNarrow
                              ? fontSizes.figure4NodeLabelsNarrow
                              : fontSizes.figure4NodeLabels
                          }
                          fontWeight={600}
                          fill={getColorByDepth(d.depth)}
                        >
                          {percentage}%
                        </text>
                        <text
                          x={d.x1 + 45}
                          y={(d.y1 + d.y0) / 2 + endNodeYOffset}
                          textAnchor="start"
                          fontSize={
                            isNarrow
                              ? fontSizes.figure4NodeLabelsNarrow
                              : fontSizes.figure4NodeLabels
                          }
                        >
                          <tspan x={d.x1 + 45} dy={labelStartOffset}>
                            {d.name || d.id}
                          </tspan>
                          {lines.map((line: string) => (
                            <tspan
                              key={line}
                              x={d.x1 + 45}
                              dy={lineSpacing}
                              fontSize={
                                isNarrow
                                  ? fontSizes.figure4NodeLabelsNarrow
                                  : fontSizes.figure4NodeLabels
                              }
                              fill="#666666"
                            >
                              {line}
                            </tspan>
                          ))}
                        </text>
                      </>
                    );
                  })()}
                </g>
              )}
            </g>
          );
        })}
      </g>
    </g>
  );

  if (embed) {
    return <g transform={`translate(0, ${translateY})`}>{content}</g>;
  }

  return (
    <svg width={width} height={height}>
      {content}
    </svg>
  );
}
